// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import { bool, number, oneOfType, string, node } from 'prop-types';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';

import useNumberUtils from 'Hooks/useNumberUtils';

const StyledContainer = styled.div`
  text-transform: uppercase;
  width: auto;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  justify-content: space-evenly;
  font-size: 0.875em;
  margin: 0 -5px;

  .badge {
    &.isEol,
    &.specialPrice {
      color: ${ props => props.theme.colors.white };
      background-color: ${ props => props.theme.products.colors.isEol };
      font-size: 13px;
    }

    &.isNew,
    &.isResealed,
    &.onDemand {
      color: ${ props => props.theme.products.colors.isResealed };
      border-color: ${ props => props.theme.products.colors.isResealed };
    }

    &.savings,
    &.isDiscount {
      color: ${ props => props.theme.colors.default };
      border-color: ${ props => props.theme.colors.default };
    }

    &.isExternalOffer,
    &.hasExternalOffer {
      color: #3749EE;
      background-color: ${ props => props.theme.colors.white };
      border-color: #3749EE;
    }

    display: flex;
    background-color: ${ props => props.theme.colors.white };
    justify-content: center;
    align-items: center;
    min-height: 20px;
    height: auto;
    border-radius: 6px;
    margin: 2px 0;
    border: 1px solid;
    padding: 2px 5px;
    font-size: 0.875rem;
    text-align: center;
    line-height: 1rem;
  }

  .specialPriceValidTo {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: inherit;
    color: ${ props => props.theme.colors.gray };
    line-height: 1rem;
    margin: 2px 5px;

    span:not(.badge) {
      white-space: nowrap;
      margin-right: 5px;
    }

    .badge {
      &.weeks {
        background-color: #F5D9C0;
      }
      &.months {
        background-color: #C9E3C7;
      }
      width: 100%;
      border: 0;
      margin: 0;
      color: ${ props => props.theme.colors.darkGray };
      background-color: ${ props => props.theme.colors.pink };
      text-align: center;
    }
  }
`;

const getDiffDays = date => {
  const date1 = new Date();
  const date2 = new Date(date);
  const diffTime = Math.abs(date2 - date1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const Badges = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { formatNumber, formatDate } = useNumberUtils();
  const {
    specialPriceValidTo = false,
    savings = 0,
    isOnDemandOnly = false,
    isNew = false,
    specialPrice = false,
    fixedPrice = false,
    isResealed = false,
    isEol = false,
    isClearance = false,
    isSupplierOffer = null,
    hasSupplierOffers = false,
    children
  } = props;
  let extraClass = '';

  if (specialPriceValidTo) {
    const days = getDiffDays(specialPriceValidTo);
    if (days < 7) {
      extraClass = 'days';
    } else if (days < 30) {
      extraClass = 'weeks';
    } else {
      extraClass = 'months';
    }
  }
  /* eslint-disable */
  const renderBadges = () => {
    if (isResealed) {
       return <span className="badge isResealed">{ t('products.isResealed') }</span>
    } else if (isSupplierOffer) {
      return <span className="badge isExternalOffer">{ t('products.supplierStock') }</span>
    } else {
      return (
        <>
          { isEol && (
            <span className="badge isEol">{ t('products.eolBadge') }</span>
          ) }
          { isClearance && (
            <span className="badge isEol isClearance">{ t('products.clearance') }</span>
          ) }
          { isNew && (
            <span className="badge isNew">{ t('products.new') }</span>
          ) }
          { isOnDemandOnly && (
            <span className="badge onDemand">{ t('products.onDemand') }</span>
          ) }
          { (specialPrice || fixedPrice) && !isClearance && (
            <span className="badge specialPrice">{ t('products.specialPrice') }</span>
          ) }
          { savings !== 0 && (
            <span className="badge savings isDiscount">
              { formatNumber(-1 * (Math.abs(savings)), { maximumSignificantDigits: 2, style: 'percent' }) }
            </span>
          ) }
          { (specialPriceValidTo && specialPrice)
            ? (
              <p className="specialPriceValidTo">
                <span>{ t('products.until') }: </span>
                <span className={ `badge ${extraClass}` }>
                  { formatDate(specialPriceValidTo, { timeStyle: undefined }) }
                </span>
              </p>
            )
            : null}
          { hasSupplierOffers && (
            <span className="badge isExternalOffer">{ t('products.otherOffers') }</span>
          ) }
        </>
      )
    }
  }

  return (
    <StyledContainer className="badges" theme={ theme } ref={ ref }>
      { renderBadges() }
      { children }
    </StyledContainer>
  );
});

Badges.propTypes = {
  children: node,
  specialPriceValidTo: oneOfType([number, string]),
  isEol: bool,
  isClearance: bool,
  isResealed: bool,
  isNew: bool,
  isOnDemandOnly: bool,
  savings: number,
  specialPrice: bool,
  fixedPrice: bool,
  isSupplierOffer: bool,
  hasSupplierOffers: bool,
};
Badges.displayName = 'Badges';

export default Badges;
